import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Crown, ShoppingBag, Check } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import api, { formatApiErrorDetail } from "../lib/api";

const TARGET_LABEL = {
  hydration: "Hydration",
  buildup: "Buildup",
  irritation: "Irritation",
  loc_stage: "Loc Stage",
};

export const ProductIcon = ({ className = "" }) => (
  <div className={`flex items-center justify-center bg-gradient-to-br from-charcoal to-black ${className}`}>
    <div className="flex h-20 w-20 items-center justify-center rounded-full border border-gold/30">
      <Crown className="h-9 w-9 text-gold" strokeWidth={1.2} />
    </div>
  </div>
);

export default function Products() {
  const [products, setProducts] = useState(null);
  const [owned, setOwned] = useState([]);
  const [buying, setBuying] = useState(null);

  const loadPurchases = () => api.get("/purchases").then((r) => setOwned(r.data.product_ids || [])).catch(() => {});

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.products)).catch(() => setProducts([]));
    loadPurchases();
  }, []);

  const money = (c) => `R${(c / 100).toFixed(2)}`;

  const buy = async (product) => {
    setBuying(product.id);
    try {
      const { data } = await api.post(`/products/${product.id}/buy`);
      if (data.paylink_url) {
        window.location.href = data.paylink_url;
      } else if (data.demo) {
        await api.post(`/payments/${data.payment_id}/confirm-demo`);
        toast.success(`${product.name} purchased!`);
        loadPurchases();
      } else {
        toast.error("Could not start checkout. Please try again.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBuying(null);
    }
  };

  return (
    <div className="min-h-screen bg-charcoal text-cream">
      <Navbar />
      <div className="mx-auto max-w-6xl px-6 py-12">
        <span className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold">
          <Crown className="h-4 w-4" /> Carlocs Culture
        </span>
        <h1 className="mt-2 font-heading text-4xl text-cream md:text-5xl">Loc scalp care</h1>
        <p className="mt-2 max-w-lg text-muted-foreground">
          The Carlocs Culture collection — purpose-built sprays and treatments recommended in your scalp reports.
        </p>

        {products === null ? (
          <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>
        ) : (
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="products-grid">
            {products.map((p, i) => {
              const isOwned = owned.includes(p.id);
              return (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.06 }}
                  className="flex flex-col overflow-hidden rounded-xl border border-charcoal-border bg-charcoal-surface"
                  data-testid={`product-${p.id}`}
                >
                  <ProductIcon className="h-52" />
                  <div className="flex flex-1 flex-col p-6">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-gold">{TARGET_LABEL[p.target] || p.target}</span>
                    <h3 className="mt-1 font-heading text-2xl leading-tight text-cream">{p.name}</h3>
                    <p className="mt-2 flex-1 text-sm text-muted-foreground">{p.description}</p>
                    <div className="mt-4 flex items-center justify-between">
                      <p className="font-heading text-2xl text-cream">{money(p.price_cents)}</p>
                      {isOwned ? (
                        <span className="flex items-center gap-1.5 rounded-full border border-gold/40 px-4 py-2 text-sm text-gold" data-testid={`owned-${p.id}`}>
                          <Check className="h-4 w-4" /> Purchased
                        </span>
                      ) : (
                        <Button
                          onClick={() => buy(p)} disabled={buying === p.id}
                          className="rounded-full bg-gold text-charcoal hover:bg-gold/90"
                          data-testid={`buy-${p.id}`}
                        >
                          {buying === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShoppingBag className="mr-2 h-4 w-4" /> Buy now</>}
                        </Button>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        <div className="mt-10 flex flex-col items-center justify-center rounded-2xl border border-dashed border-charcoal-border bg-charcoal-surface py-14 text-center" data-testid="products-coming-soon">
          <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-full border border-gold/30 text-gold"><Crown className="h-5 w-5" strokeWidth={1.4} /></span>
          <h3 className="font-heading text-2xl text-cream">More products coming soon</h3>
          <p className="mt-1 text-sm text-muted-foreground">New Carlocs Culture additions are on the way.</p>
        </div>
      </div>
      <Footer />
    </div>
  );
}
